// Source: https://usehooks-ts.com/react-hook/use-debounce-callback.
import debounce from 'lodash.debounce';
import { useEffect, useMemo, useRef } from 'react';
import useUnmount from './use-unmount.js';
/**
 * Custom hook that creates a debounced version of a callback function.
 */
function useDebounceCallback(fn, delay = 500, options) {
    const debouncedFn = useRef();
    useUnmount(() => {
        if (debouncedFn.current) {
            debouncedFn.current.cancel();
        }
    });
    const debounced = useMemo(() => {
        const debouncedFnInstance = debounce(fn, delay, options);
        const wrappedFn = (...args) => {
            return debouncedFnInstance(...args);
        };
        wrappedFn.cancel = () => {
            debouncedFnInstance.cancel();
        };
        wrappedFn.isPending = () => {
            return !!debouncedFn.current;
        };
        wrappedFn.flush = () => {
            return debouncedFnInstance.flush();
        };
        return wrappedFn;
    }, [fn, delay, options]);
    // Update the debounced function ref whenever fn, wait, or options change.
    useEffect(() => {
        debouncedFn.current = debounce(fn, delay, options);
    }, [fn, delay, options]);
    return debounced;
}
export default useDebounceCallback;
